/**
 * What a withdrawal's MAX button resolves to.
 *
 * Ported from `WithdrawModal.helpers.ts` (`collateralSpendableUnits`,
 * `computeWithdrawableUnits`), worked in exact decimals instead of base-unit
 * BigNumbers, and returning a reason rather than `undefined` when the maximum
 * cannot be known.
 *
 * One bug is fixed on the way across. The account-level `withdrawable` is a USD
 * figure; the old code compared it with a TOKEN figure in base units. For USDC
 * that happens to be right — a dollar is a dollar — but for WBTC it compared
 * "$12,000" with "0.5 BTC", concluded the cap did not bind, and offered the
 * whole balance when only 0.2 BTC could actually leave. Here a non-USD cap is
 * converted at the price the collateral row itself implies
 * (`collateral_value / balance`); when that price cannot be derived the maximum
 * is unknown, never uncapped.
 */
import { Dec, dec } from "../decimal";
import { MaxResult, MaxUnavailableReason, maxOk, maxUnavailable } from "./types";

/** The fields of an account's per-collateral row that bear on a maximum. */
export interface CollateralRow {
  /** Token amount held. */
  balance?: Dec | null;
  /** Token amount not already committed; absent means "all of it". */
  available_balance?: Dec | null;
  /** USD value of the whole `balance` — the only price this row carries. */
  collateral_value?: Dec | null;
}

export interface PerpWithdrawMaxInput {
  collateral?: CollateralRow;
  /**
   * The account's margin-aware `withdrawable`, in USD. `null` is the backend
   * saying it cannot value the account; `undefined` is "no cap supplied".
   */
  withdrawableUsd?: Dec | null;
  /** The token's decimals — the grid every figure here is floored to. */
  decimals: number;
  /** Whether this collateral is a USD stablecoin (1 token = $1). */
  isUsdStable: boolean;
  /** Account-level credit, a USD line; only ever charged against a stablecoin. */
  credit?: Dec | null;
  /** Shown beside the figure: "USDC", "WBTC". */
  unit?: string;
}

export interface SpotWithdrawMaxInput {
  /** Free balance held by the spot engine, in base units; absent = unread. */
  onchainFreeUnits?: bigint;
  decimals: number;
  unit?: string;
}

const blank = (value: unknown): boolean =>
  value === null || value === undefined || value === "";

/** A figure floored to the token's grid. Negative is zero; unreadable is null. */
const floorAmount = (value: Dec | null | undefined, decimals: number): Dec | null => {
  const parsed = dec.parse(blank(value) ? null : value);
  if (parsed === null) return null;
  return dec.isNeg(parsed) ? "0" : dec.round(parsed, decimals, "floor");
};

/** A figure that will be SUBTRACTED, ceiled so the result can never overstate. */
const ceilAmount = (value: Dec | null | undefined, decimals: number): Dec | null => {
  const parsed = dec.parse(blank(value) ? null : value);
  if (parsed === null) return null;
  return dec.isNeg(parsed) ? "0" : dec.round(parsed, decimals, "ceil");
};

/**
 * `min(balance, available) - credit`, floored to the token's decimals and
 * clamped at zero: the collateral figure both withdraw and transfer start from.
 * `null` when the balance cannot be read — which is never the same as zero.
 */
export const collateralSpendable = (input: PerpWithdrawMaxInput): Dec | null => {
  const row = input.collateral;
  if (!row) return null;

  const balance = floorAmount(row.balance, input.decimals);
  if (balance === null) return null;

  // An absent `available_balance` means nothing is committed; a present one that
  // cannot be read is a genuine unknown.
  const available = blank(row.available_balance)
    ? balance
    : floorAmount(row.available_balance, input.decimals);
  if (available === null) return null;

  const credit = input.isUsdStable
    ? ceilAmount(input.credit, input.decimals) ?? "0"
    : "0";

  const spendable = dec.sub(dec.min(balance, available), credit);
  return dec.isNeg(spendable) ? "0" : spendable;
};

/**
 * The account's USD `withdrawable` expressed in this token, floored to its
 * decimals. Returns `undefined` when no cap was supplied, or a reason when it
 * cannot be expressed.
 *
 * The conversion divides by the row's own price — `collateral_value / balance` —
 * but computes the result as `usd x balance / collateral_value` so the division
 * rounds exactly once; dividing by a rounded price can lose the last base unit.
 */
const capInTokens = (
  input: PerpWithdrawMaxInput
): Dec | MaxUnavailableReason | undefined => {
  if (input.withdrawableUsd === undefined) return undefined;
  if (input.withdrawableUsd === null) return "withdrawable_unknown";

  const usd = dec.parse(input.withdrawableUsd);
  if (usd === null) return "withdrawable_unknown";
  if (dec.isNeg(usd)) return "0";

  if (input.isUsdStable) return dec.round(usd, input.decimals, "floor");

  const balance = dec.parse(input.collateral?.balance ?? null);
  const value = dec.parse(input.collateral?.collateral_value ?? null);
  if (balance === null || value === null) return "price_unknown";
  if (!dec.gt(balance, "0") || !dec.gt(value, "0")) return "price_unknown";

  const tokens = dec.div(dec.mul(usd, balance), value, input.decimals, "floor");
  return tokens === null ? "price_unknown" : tokens;
};

const isReason = (
  value: Dec | MaxUnavailableReason | undefined
): value is MaxUnavailableReason =>
  typeof value === "string" && dec.parse(value) === null;

/**
 * The most of `collateral` that may leave the exchange: the spendable balance,
 * capped by what margin still allows.
 */
export const perpWithdrawMax = (input: PerpWithdrawMaxInput): MaxResult => {
  const unit = input.unit ?? "";

  // Checked before the balance: with an unknown withdrawable no figure can be
  // offered, however readable the collateral row is.
  if (input.withdrawableUsd === null) {
    return maxUnavailable("withdrawable_unknown");
  }

  const spendable = collateralSpendable(input);
  if (spendable === null) return maxUnavailable("balance_unknown");

  const cap = capInTokens(input);
  if (isReason(cap)) return maxUnavailable(cap);
  if (cap === undefined) return maxOk(spendable, "balance", unit);

  return dec.lt(cap, spendable)
    ? maxOk(cap, "withdrawable", unit)
    : maxOk(spendable, "balance", unit);
};

/**
 * The spot engine holds its own free balance on chain; there is no margin to
 * account for, so the balance IS the maximum.
 */
export const spotWithdrawMax = (input: SpotWithdrawMaxInput): MaxResult => {
  if (input.onchainFreeUnits === undefined) {
    return maxUnavailable("balance_unknown");
  }
  const amount = dec.fromUnits(input.onchainFreeUnits, input.decimals);
  const floored = dec.round(amount, input.decimals, "floor");
  return maxOk(
    dec.isNeg(floored) ? "0" : floored,
    "balance",
    input.unit ?? ""
  );
};
